import Vue from "vue";

import { AsyncModule } from "vuex-async-mutations";
import {
  FormattedDocument,
  MechanicInstallation,
  InstallationModule,
  DeliveryDocumentModuleType,
  DynamicDocumentMeta,
  Profile,
  DynamicDocumentItemData,
  DynamicDocument,
  DynamicDocumentItem,
  DynamicDocuments,
  DynamicDocumentGroup,
  InstallationModuleData
} from "@/types";
import documentFactory from "@/store/modules/deliveryDocuments/documents";
import dynamicDocuments from "@/store/modules/deliveryDocuments/dynamic-documents";
import installationDocuments from "@/store/modules/deliveryDocuments/installation";
import validators from "./validators";
import customValidators from "./validators/custom";
import store from "@/store/store";

import parse from "./parse";
import { TrackJS } from "trackjs";
import { isItemRequired } from "@/utilities";
import { isGroupVisible, isItemVisible } from "@/utilities/dynamic-document";

function CustomDepends(moduleData: InstallationModuleData) {
  return function isVisible(item: DynamicDocumentItem | undefined) {
    if (!item?.customDepends) return true;

    const customFunctions = {
      imageSealcaps: imageSealcaps(moduleData),
      moreThan6Panels: moreThan6Panels(moduleData)
    };

    return (customFunctions as any)[item?.customDepends];
  };
}

function moreThan6Panels(moduleData: InstallationModuleData) {
  return moduleData?.numberOfPanels && moduleData?.numberOfPanels > 6;
}

function imageSealcaps(moduleData: InstallationModuleData) {
  const panelArray = moduleData?.inverterConfiguration?.flatMap(
    ({ amount, max_panel_count }) => amount * max_panel_count
  );

  const maxPanelCount = panelArray?.reduce(
    (prevValue, currentValue) => prevValue + currentValue,
    0
  );

  const inverter = store.getters["materials/inverterConfigurations/findItem"](
    moduleData?.inverterConfigurationId
  );
  const numberOfPanels = moduleData?.numberOfPanels;
  const isAps = inverter?.name.toLowerCase().includes("aps");
  const isHoymiles = inverter?.name.toLowerCase().includes("hoymiles");
  const hasOffSet = Math.abs((numberOfPanels || 0) - (maxPanelCount || 0)) >= 1 || false;

  return (isAps || isHoymiles) && hasOffSet;
}
interface DeliveryDocumentsState {
  documents: {
    ["METER-REGULAR"]: DynamicDocuments;
    ["METER_MAAS-REGULAR"]: DynamicDocuments;
    ["PANELS-REGULAR"]: DynamicDocuments;
    ["TENANT"]: DynamicDocuments;
  };
}

const stateFactory = (): DeliveryDocumentsState => ({
  documents: {
    ["METER-REGULAR"]: {},
    ["METER_MAAS-REGULAR"]: {},
    ["PANELS-REGULAR"]: {},
    ["TENANT"]: {}
  }
});

export const module: AsyncModule<DeliveryDocumentsState, any> = {
  namespaced: true,

  async: true,

  state: stateFactory,

  getters: {
    ["document"](state) {
      return (installationId: number, moduleType: DeliveryDocumentModuleType) => {
        return state.documents[moduleType][installationId];
      };
    },

    ["data"](state) {
      return (installationId: number, moduleType: DeliveryDocumentModuleType) => {
        const doc = state.documents[moduleType][installationId];

        return doc?.data ?? [];
      };
    },

    ["origin"](state) {
      return (installationId: number, moduleType: DeliveryDocumentModuleType) => {
        const doc = state.documents[moduleType][installationId];

        return doc?.origin ?? [];
      };
    },

    ["dynamicDocument"](state, getters, rootState, rootGetters) {
      return (
        installationId: number,
        moduleType: DeliveryDocumentModuleType
      ): DynamicDocumentGroup[] => {
        const data: DynamicDocumentItemData[] = getters.data(installationId, moduleType);
        const module = rootGetters["installation/module"](installationId, moduleType);
        const installation: MechanicInstallation = rootGetters["installations/details"](
          installationId
        );
        const groups = dynamicDocuments[moduleType];
        const items = groups.flatMap(group => group.items);

        return groups
          .filter(doc => !doc.required || installation.options[doc.required])
          .filter(group => isGroupVisible(group, groups, data, module?.data))
          .map(group => {
            const isVisible = isItemVisible(items, data, module?.data);
            return { ...group, items: group.items.filter(isVisible) };
          })
          .map(group => {
            const isVisible = CustomDepends(module?.data);
            return { ...group, items: group.items.filter(isVisible) };
          })
          .filter(group => group.items.length > 0);
      };
    },

    ["dynamicDocumentData"](state, getters, rootState, rootGetters) {
      return (
        installationId: number,
        moduleType: DeliveryDocumentModuleType
      ): DynamicDocumentItemData[] => {
        const sources: DynamicDocumentGroup[] = dynamicDocuments[moduleType];
        const localData: DynamicDocumentItemData[] = getters.data(installationId, moduleType);
        const originData: DynamicDocumentItemData[] = getters.origin(installationId, moduleType);

        const module: InstallationModule | undefined = rootGetters["installation/module"](
          installationId,
          moduleType
        );
        const profile: Profile = rootState.profile.user;
        const installation: MechanicInstallation = rootGetters["installations/details"](
          installationId
        );

        const safeModuleData: InstallationModuleData = (module?.data ??
          {}) as InstallationModuleData;

        let source: any;

        function getValue(item: DynamicDocumentItem) {
          switch (item.fill?.source) {
            case "INSTALLATION":
              source = installation;
              break;
            case "MODULE":
              source = module;
              break;
            case "PROFILE":
              source = profile;
              break;
            default:
              return undefined;
          }

          return source?.[item.fill.field];
        }

        function mapItem(item: DynamicDocumentItem): DynamicDocumentItemData {
          const local = localData.find(currentItem => currentItem.field === item.field);
          const origin = originData.find(currentItem => currentItem.field === item.field)!;

          const moduleData = safeModuleData[item.field];

          let value = local?.value;

          if (value === undefined) {
            if (item.fill) {
              value = getValue(item);
            } else {
              value = moduleData;
            }
          }

          const validationData = local || Object.assign({}, item, { value });

          const baseData: InstallationModuleData = {
            ...safeModuleData
          } as InstallationModuleData;

          let augmentedData: InstallationModuleData = baseData;

          if (!(typeof baseData.numberOfPanels === "number" && baseData.numberOfPanels > 0)) {
            const panelsModule = (installation.modules || []).find(
              m => m.type === "PANELS-REGULAR"
            );
            const fromPanels =
              panelsModule && panelsModule.data ? panelsModule.data.numberOfPanels : undefined;
            const fromScreening =
              installation && installation.screening
                ? installation.screening.numberOfPanels
                : undefined;
            augmentedData = {
              ...baseData,
              numberOfPanels: baseData.numberOfPanels ?? fromPanels ?? fromScreening
            } as InstallationModuleData;
          }

          const augmentedModule: InstallationModule = {
            ...(module || {}),
            data: augmentedData
          } as InstallationModule;

          const validation =
            item.type === "CUSTOM"
              ? customValidators(item, augmentedModule)
              : validators(item, validationData);

          const isRequired = isItemRequired(item, localData, safeModuleData);
          const isValid = !isRequired || validation === true;

          return {
            field: item.field,
            comment: local?.comment,
            value: value === undefined ? parse.getDefaultValue(item) : value,
            isValid,
            validation,
            hasChanges: !parse.areEqual(local, origin)
          };
        }

        return sources.flatMap(source => source.items.map(mapItem));
      };
    },

    ["hasChanges"](state) {
      return (installationId: number, moduleType: DeliveryDocumentModuleType) => {
        const doc = state.documents[moduleType][installationId];

        return doc?.changedAt && (!doc.meta.updatedAt || doc.changedAt > doc.meta.updatedAt);
      };
    },

    ["updatedAt"](state) {
      return (installationId: number, moduleType: DeliveryDocumentModuleType) => {
        const doc = state.documents[moduleType][installationId];

        return doc?.meta?.updatedAt ?? -1;
      };
    },

    ["endedAt"](state) {
      return (installationId: number, moduleType: DeliveryDocumentModuleType) => {
        const doc = state.documents[moduleType][installationId];

        return doc?.meta?.endedAt ?? -1;
      };
    }
  },

  mutations: {
    ["update"](
      state,
      {
        id,
        moduleType,
        items,
        meta
      }: {
        id: number;
        moduleType: DeliveryDocumentModuleType;
        items: DynamicDocumentItemData[];
        meta: DynamicDocumentMeta;
      }
    ) {
      Vue.set(state.documents[moduleType], id, {
        data: items,
        origin: state.documents[moduleType]?.[id]?.origin,
        changedAt: Date.now(),
        meta: { ...state.documents[moduleType]?.[id]?.meta, ...meta }
      } as DynamicDocument);
    },

    ["reset"](state) {
      Object.assign(state, stateFactory());
    }
  },

  actionsAsync: {
    ["end"]: {
      async handler(
        { commitAsync, dispatch },
        { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
      ) {
        await commitAsync(Promise.resolve(), { id, moduleType });
        await dispatch("save", { id, moduleType });

        if (moduleType === "TENANT") {
          await commitAsync(
            this.$axios.$post(`/installations/${id}/delivery-document/generate-pdf/tenant`),
            { id, moduleType }
          );
        }
      },

      resolved(
        state,
        _payload,
        { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
      ) {
        state.documents[moduleType][id].meta.endedAt = Date.now();
      }
    },

    ["fetch"]: {
      handler(
        { commitAsync },
        { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
      ) {
        return commitAsync(
          this.$axios.$get(`/installations/${id}/delivery-document/${moduleType}`),
          {
            id,
            moduleType
          }
        );
      },

      resolved(
        state,
        payload: DynamicDocumentItemData[] | DynamicDocument,
        { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
      ) {
        const document = state.documents[moduleType][id];
        let data: DynamicDocumentItemData[];
        let meta: DynamicDocumentMeta = { updatedAt: -1 };

        if (payload instanceof Array) {
          data = payload;
        } else {
          data = payload.data;
          meta = payload.meta ?? meta;
        }

        if (!document || meta.updatedAt > document.meta.updatedAt) {
          Vue.set(state.documents[moduleType], id, {
            origin: data ?? [],
            data: data ?? [],
            meta: meta ?? {},
            changedAt: null
          });
        } else {
          state.documents[moduleType][id].origin = data ?? [];
        }
      }
    },

    ["store"]: {
      async handler(
        { getters },
        { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
      ) {
        const updatedAt = Date.now();
        const documentData: DynamicDocument = getters["document"](id, moduleType);

        await this.$axios.put(`/installations/${id}/delivery-document/${moduleType}`, {
          data: documentData.data,
          changedAt: documentData.changedAt,
          meta: { ...documentData.meta, updatedAt }
        } as DynamicDocument);

        return updatedAt;
      }
    },

    ["save"]: {
      async handler(
        { commitAsync, dispatch, getters, rootGetters, rootState },
        { id, moduleType }: { id: number; moduleType: DeliveryDocumentModuleType }
      ) {
        const documentData: DynamicDocument = getters["document"](id, moduleType);
        const document: DynamicDocumentGroup[] = getters["dynamicDocument"](id, moduleType);
        const installation: MechanicInstallation = rootGetters["installation/installation"](id);

        const installationModule: InstallationModule = rootGetters["installation/module"](
          id,
          moduleType
        );

        const updatedAt: number = await dispatch("store", { id, moduleType });

        const installationDocument: FormattedDocument = installationDocuments.general(
          installation,
          rootState.profile.user,
          installationModule
        );
        const moduleInstallationDocument: FormattedDocument = installationDocuments[moduleType](
          installationModule,
          documentData.data
        );

        const moduleDocument: FormattedDocument = documentFactory(
          document,
          documentData.data,
          installationModule.data
        );

        try {
          return commitAsync(
            this.$axios.$post(
              `/installations/${id}/delivery-document/${
                moduleType === "TENANT" ? "tenant" : `delivery/${moduleType}`
              }`,
              [...installationDocument, ...moduleInstallationDocument, ...moduleDocument]
            ),
            {
              id,
              moduleType,
              updatedAt
            }
          );
        } catch (error) {
          if (error.response) {
            TrackJS.addMetadata("Response", error.response.data);
            TrackJS.addMetadata(
              "Payload",
              JSON.stringify([
                ...installationDocument,
                ...moduleInstallationDocument,
                ...moduleDocument
              ])
            );
          }
        }
      },

      resolved(
        state,
        _payload,
        {
          id,
          moduleType,
          updatedAt
        }: { id: number; moduleType: DeliveryDocumentModuleType; updatedAt: number }
      ) {
        state.documents[moduleType][id].meta.updatedAt = updatedAt;
        state.documents[moduleType][id].origin = state.documents[moduleType][id].data;
      }
    }
  },

  actions: {
    ["update"](
      { commit },
      {
        id,
        data,
        moduleType
      }: {
        id: number;
        data: DynamicDocumentItemData[];
        moduleType: DeliveryDocumentModuleType;
      }
    ) {
      commit("update", {
        id,
        moduleType,
        items: data.map(({ field, value, comment }) => ({ field, value, comment })),
        meta: {}
      });
    },

    ["reset"]: {
      root: true,
      handler({ commit }) {
        commit("reset");
      }
    }
  }
};

export default module;
